import { createWebHistory, createRouter } from "vue-router";
import { useAuthStore } from '@/stores/authStore';
import routes from './routes';
import appConfig from "../../app.config";

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, left: 0 };
    }
  },
});

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  // Set page title
  document.title = to.meta.title ? `${to.meta.title} | ${appConfig.title}` : appConfig.title;

  // Check if route requires authentication
  const authRequired = to.matched.some((route) => route.meta.authRequired);

  // Handle logout route
  if (to.name === 'logout') {
    await authStore.logout();
    return next({ name: 'login' });
  }

  // If auth not required, allow access
  if (!authRequired) {
    // If already authenticated and trying to access login/forgot-password, redirect to home
    if (authStore.isAuthenticated && (to.name === 'login' || to.name === 'forgot-password')) {
      return next({ name: 'home' });
    }
    return next();
  }

  // Auth required - check if user is authenticated
  if (!authStore.isAuthenticated) {
    return next({
      name: 'login',
      query: { redirectFrom: to.fullPath }
    });
  }

  // Verify token is still valid by fetching user if not already loaded
  try {
    if (!authStore.user) {
      await authStore.fetchUser();
    }

    // Check permission if route requires it
    // ⚠️ SECURITY NOTE: This is a UX optimization only!
    // Server-side authorization is REQUIRED and enforced via Laravel middleware
    // on all API endpoints. Never rely on client-side checks for security.
    // Attackers can bypass this by manipulating localStorage or browser state.
    const requiredPermission = to.meta.permission;
    if (requiredPermission && !authStore.hasPermission(requiredPermission)) {
      // User doesn't have required permission - redirect to forbidden page
      return next({ name: 'error-forbidden' });
    }

    next();
  } catch (error) {
    // Token invalid - redirect to login
    await authStore.logout();
    next({
      name: 'login',
      query: { redirectFrom: to.fullPath }
    });
  }
});

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg');
  if (appLoading) {
    appLoading.style.display = "none";
  }
});

export default router;
